import { useState } from 'react';
import {
    ALLIANCE_DESCRIPTION_MAX_LENGTH,
    ALLIANCE_NAME_MAX_LENGTH,
    ALLIANCE_NAME_MIN_LENGTH,
    ALLIANCE_REQUIRED_BUILDING_LEVEL,
    ALLIANCE_TAG_MAX_LENGTH,
    ALLIANCE_TAG_MIN_LENGTH,
    BUILDING_NAMES,
} from '@kaeliux/shared';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { cn } from '../utils/cn';
import { FIELD_BASE_CLASSES, FIELD_FOCUS_CLASSES, FIELD_LABEL_CLASSES } from './ui/fieldStyles';

interface AllianceCreateFormProps {
    onCreate: (input: { name: string; tag: string; description: string }) => Promise<void>;
    isSubmitting: boolean;
    error: string | null;
}

/**
 * Founding form. The Team-building prerequisite is NOT pre-checked here —
 * the server (and the mock, which runs the same engine rule) rejects with
 * the exact reason, which is rendered verbatim. Duplicating the check
 * client-side would mean two places to keep in step for no gain, since the
 * button still has to handle the server's answer.
 */
export default function AllianceCreateForm({ onCreate, isSubmitting, error }: AllianceCreateFormProps) {
    const [name, setName] = useState('');
    const [tag, setTag] = useState('');
    const [description, setDescription] = useState('');

    const canSubmit =
        name.trim().length >= ALLIANCE_NAME_MIN_LENGTH &&
        tag.trim().length >= ALLIANCE_TAG_MIN_LENGTH &&
        !isSubmitting;

    return (
        <form
            className="flex flex-col gap-3"
            data-testid="alliance-create-form"
            onSubmit={(e) => {
                e.preventDefault();
                if (!canSubmit) return;
                void onCreate({ name: name.trim(), tag: tag.trim(), description: description.trim() });
            }}
        >
            <h3 className="text-title text-content-primary">Found an Alliance</h3>
            <p className="text-content-muted text-label">
                Requires the {BUILDING_NAMES.government} at Level{' '}
                {ALLIANCE_REQUIRED_BUILDING_LEVEL} on one of your planets.
            </p>

            <Input
                label="Name"
                value={name}
                maxLength={ALLIANCE_NAME_MAX_LENGTH}
                placeholder="Legio Astra"
                aria-label="Alliance name"
                onChange={(e) => setName(e.target.value)}
            />

            {/* Hand-styled like the Description textarea below: `uppercase` must
                land on the input, not on the Input wrapper where it would also
                transform the label. */}
            <label className="flex flex-col gap-1">
                <span className={FIELD_LABEL_CLASSES}>
                    Tag ({ALLIANCE_TAG_MIN_LENGTH}-{ALLIANCE_TAG_MAX_LENGTH} characters)
                </span>
                <input
                    className={cn(FIELD_BASE_CLASSES, FIELD_FOCUS_CLASSES, 'uppercase')}
                    value={tag}
                    maxLength={ALLIANCE_TAG_MAX_LENGTH}
                    placeholder="LEG"
                    aria-label="Alliance tag"
                    onChange={(e) => setTag(e.target.value)}
                />
            </label>

            <label className="flex flex-col gap-1">
                <span className="text-label text-content-secondary">Description (optional)</span>
                <textarea
                    className={cn(FIELD_BASE_CLASSES, FIELD_FOCUS_CLASSES, 'h-auto min-h-24 py-2')}
                    value={description}
                    maxLength={ALLIANCE_DESCRIPTION_MAX_LENGTH}
                    placeholder="Who you are and who you are looking for."
                    aria-label="Alliance description"
                    onChange={(e) => setDescription(e.target.value)}
                />
            </label>

            {error && <p className="text-status-danger text-label" role="alert">{error}</p>}

            <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={isSubmitting}
                disabled={!canSubmit}
            >
                Found Alliance
            </Button>
        </form>
    );
}
